import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import posthog from "posthog-js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SessionContextProvider } from "@supabase/auth-helpers-react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import AppRoutes from "./router";
import "./index.css";
import "./i18n"; // Import i18n configuration
import * as Sentry from "@sentry/react";
import { useThemeStore } from "@/stores/use-theme-store";
import i18next from "i18next"; // Add this import if not already present
import { SentryUserProvider } from "@/components/providers/SentryUserProvider";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { UserbackProvider } from "./components/providers/UserBackProvider";

const initialTheme = useThemeStore.getState().theme;
const currentLanguage = i18next.language;

const feedbackFormTranslations = {
  ru: {
    triggerLabel: "Обратная связь / Помощь",
    formTitle: "Оставить отзыв",
    nameLabel: "Полное имя",
    namePlaceholder: "Введите ваше полное имя",
    emailLabel: "Email",
    emailPlaceholder: "Введите ваш email",
    messageLabel: "Описание (Обязательно)",
    messagePlaceholder: "",
    isRequiredLabel: "Обязательное поле",
    addScreenshotButtonLabel: "Добавить скриншот",
    removeScreenshotButtonLabel: "Удалить скриншот",
    submitButtonLabel: "Отправить отчет об ошибке",
    cancelButtonLabel: "Отмена",
    successMessage: "Спасибо за ваш отчет!",
  },
  en: {
    triggerLabel: "Feedback / Help ",
    messagePlaceholder: "",
    // Default English labels will be used if not specified
  },
};

// Initialize Sentry with custom configuration
// Note: User data is injected via the SentryUserProvider component
// which uses the useSentryUser hook to sync Supabase auth state with Sentry
Sentry.init({
  dsn: "https://92a3de5b4f468abccf39010e1934aec9@o4508744360656896.ingest.de.sentry.io/4508744363475024",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),

    // Sentry.feedbackIntegration({
    //   // User data is handled by the SentryUserProvider component
    //   showBranding: false,
    //   showName: false,
    //   colorScheme: initialTheme,
    //   themeLight: {
    //     accentBackground: "#4D97E0",
    //   },
    //   themeDark: {
    //     accentBackground: "#4D97E0",
    //   },
    //   ...feedbackFormTranslations[currentLanguage === 'ru' ? 'ru' : 'en']
    // }),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

posthog.init("phc_fKHD8kfBToyCuU29jhrtvq5z1UUKdMWGumCdlgcjUZD", {
  api_host: "https://eu.i.posthog.com",
  person_profiles: "identified_only",
});

const tanstackClient = new QueryClient();

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={tanstackClient}>
      <SessionContextProvider supabaseClient={supabase}>
        <TooltipProvider>
          <SubscriptionProvider>
            <BrowserRouter>
              <SentryUserProvider>
                <UserbackProvider>
                  <AppRoutes />
                </UserbackProvider>
              </SentryUserProvider>
            </BrowserRouter>
            <Toaster />
          </SubscriptionProvider>
        </TooltipProvider>
      </SessionContextProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
